/* eslint-disable camelcase */
import { FountainLocation } from '@fountain/types/base';
import { createSelector } from 'reselect';

import { ADMIN_ROLE } from './constants';
import { initialState } from './reducer';
/**
 * Direct selector to the auth state domain
 */
export type SourcingDescriptionSource =
  | 'custom_description'
  | 'position_description';

interface ReduxFountainLocation extends FountainLocation {
  isSelected?: boolean;
}
export interface Brand {
  name: string;
  slug: string;
  id: string;
  brand_id: number;
  logo_url: string;
  locations: ReduxFountainLocation[];
  isAccount: boolean;
  industry?: unknown;
  default?: boolean;
  ax_enabled: boolean;
  isSelected: boolean;
}
export interface WhoAmI {
  agent_number: string;
  email: string;
  name: string;
  role: string;
  menu: string[];
  brands: Brand[];
  phone_number?: string;
  is_faq_bot_enabled: boolean;
  is_impersonated: boolean;
  is_owner: boolean;
  is_super_admin: boolean;
  true_user_email: string;
  external_id: string;
  wotc_enabled: boolean;
  batch_csv_enabled: boolean;
  whats_app_enabled: boolean;
  time_based_duplicate_handling: boolean;
  chatbot_admin_enabled: boolean;
  auto_generate_dummy_email_enabled: boolean;
  chatbot_review_enabled: boolean;
  chatbot_automated_response_enabled: boolean;
  fountain_ai_enabled: boolean;
  cai_agent_enabled: boolean;
  fountain_ai_faq_enabled: boolean;
  has_sub_accounts: boolean;
  post_interview_recruiter_notifications_enabled: boolean;
  slot_availability_based_openings_enabled: boolean;
  use_applicant_table_v2_only: boolean;
  accessible_account_slugs: string[];
  account_id: string;
  account_name: string;
  account_name_v2: string;
  account_type: string;
  account_sms_enabled: boolean;
  account_has_legacy_workflows: boolean;
  account_has_workflows: boolean;
  bulk_locations_beta: boolean;
  perms: string[];
  current_funnel_slug: string;
  current_funnel_id: string;
  keywords_purchased: number;
  time_zone: string;
  user_type: string;
  country_code: string;
  client_settings: {
    user: unknown;
    quickActions: boolean;
    scheduleSessionColumnSettings?: {
      key: string;
      title: string;
      category: string;
      key_type: string;
    }[];
  };
  enforce_brand_based_permissions: boolean;
  email_for_messenger_enabled: boolean;
  opening_approval_enabled: boolean;
  opening_approval_control_enabled?: boolean;
  policies: {
    manage_account: boolean;
    view_messaging_thread: boolean;
    view_jobs_directory: boolean;
    manage_funnels: boolean;
    view_funnels: boolean;
    manage_workflows: boolean;
    view_workflows: boolean;
    manage_opening_approvers: boolean;
    manage_approval_rules: boolean;
    approve_offers: boolean;
    approve_openings: boolean;
  };
  posthire_enabled: boolean;
  analytics_enabled: boolean;
  omni_analytics_enabled: boolean;
  looker_analytics_enabled: boolean;
  looker_custom_reports_enabled: boolean;
  created_at: string;
  last_sign_in_at: string;
  recruiter_ui_env_vars: {
    REACT_APP_CHARGEBEE_ANNUAL_PLAN_ID: string;
    REACT_APP_CHARGEBEE_MONTHLY_PLAN_ID: string;
    REACT_APP_FACEBOOK_APP_ID: string;
    REACT_APP_HELLOSIGN_CLIENT_ID: string;
    REACT_APP_PUSHER_APP_KEY: string;
    REACT_APP_PUSHER_CLUSTER: string;
    REACT_APP_STRIPE_KEY: string;
    REACT_APP_ONESIGNAL_WEB_PUSH_APP_ID: string;
  };
  bulk_editing_enabled: boolean;
  unread_sms: boolean;
  get_more_text_to_apply_enabled: boolean;
  hiring_goals_enabled: boolean;
  hiring_goals_v3_enabled: boolean;
  locale?: string;
  desktop_notification_enabled: boolean;
  has_restricted_funnel_access: boolean;
  first_job_id?: unknown;
  go_enabled: boolean;
  instant_interview_enabled: boolean;
  immediate_hiring_decision_enabled: boolean;
  intercom_digest?: string;
  workflow_editor_v2_enabled: boolean;
  workflow_system_enabled: boolean;
  account_unified_auth?: boolean;
  feature_flags: {
    [key: string]: boolean;
  };
  docusign_qes_feature_enabled: boolean;
  fountain_ai_carreer_site_setting_enabled: boolean;
  fountain_ai_career_site_enabled: boolean;
  selected_account_slug: string;
  wx_url?: string | null;
  account_wx_domain?: string | null;
  source_url?: string | null;
  default_brand_id: string;
  funnel_status_based_off_slots_enabled: boolean;
  qr_code_for_opening_enabled: boolean;
  can_create_standard_workflow_templates: boolean;
  calendar_event_creation_enabled: boolean;
  event_adaptive_enabled: boolean;
  max_availability_occurrences: number;
  can_send_only_templates_in_sms: boolean;
  go_user: boolean;
  wx_applicant_sync_webhooks_exist: boolean;
  close_on_target_date_enabled: boolean;
  close_once_goal_is_met_enabled: boolean;
  deactivate_funnel_when_goals_closed_enabled: boolean;
  hide_inactive_openings: boolean;
  require_active_hiring_goal_for_openings: boolean;
  hire_by_end_of_day_enabled: boolean;
  data_pipeline: boolean;
  data_pipeline_applicants: boolean;
}
interface User {
  isSelected?: boolean;
}
interface SuperAdmin {
  accounts: unknown;
  users: User[];
}
interface Auth {
  error?: Error;
  whoami: WhoAmI;
  signedOut: boolean;
  isLoading: boolean;
  superAdmin: SuperAdmin;
  isUpdatingClientSettings: boolean;
  clientSettingsUpdateSuccess: boolean;
  isFetchingAllBrands: boolean;
  fetchingAllBrandsError?: Error;
  isAllBrandsFetched: boolean;
}

const selectAuthDomain = (state: { auth: Auth }) => state.auth || initialState;

const selectBrands = (state: { auth: Auth }) => state.auth.whoami.brands;

const makeSelectWhoami = () =>
  createSelector(selectAuthDomain, authState => authState.whoami);

const makeSelectSelectedAccountSlug = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.selected_account_slug);

const makeSelectAccessibleAccountSlugs = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.accessible_account_slugs,
  );

/**
 * Other specific selectors
 */
const makeSelectUserName = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.name);

const makeSelectPhoneNumber = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.phone_number);

const makeSelectEmail = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.email);

const makeSelectExternalId = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.external_id);

const makeSelectUnreadSms = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.unread_sms);

const makeSelectAccountName = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.account_name_v2);

const makeSelectIsUpdatingClientSettings = () =>
  createSelector(
    selectAuthDomain,
    authState => authState.isUpdatingClientSettings,
  );

const makeSelectClientSettings = () =>
  createSelector([makeSelectWhoami()], whoami => ({
    clientSettings: whoami.client_settings,
  }));

const makeSelectIsLoggedIn = () =>
  createSelector(
    [makeSelectWhoami(), makeSelectUserName(), makeSelectEmail()],
    (whoami, name, email) => !!whoami && !!name && !!email,
  );

const makeSelectWhoamiError = () =>
  createSelector(selectAuthDomain, authState => authState.error);

const makeSelectBrands = () => createSelector(selectBrands, brands => brands);

const makeIsAllBrandsFetched = () =>
  createSelector(selectAuthDomain, authState => authState.isAllBrandsFetched);

const makeIsFetchingAllBrands = () =>
  createSelector(selectAuthDomain, authState => authState.isFetchingAllBrands);

const makeSelectLocations = () =>
  createSelector([makeSelectBrands()], brands => {
    const selectedBrand = brands.find(brand => brand.isSelected);
    return selectedBrand ? selectedBrand.locations : [];
  });

const makeSelectCurrentLocation = () =>
  createSelector([makeSelectLocations()], locations => {
    if (locations && locations.length)
      return locations.find(loc => loc.isSelected);
    return {};
  });

const makeSelectAccountSmsEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.account_sms_enabled);

const makeSelectTelephonyEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => !!(whoami.feature_flags.telephony && whoami.agent_number),
  );

const makeSelectAccountHasWorkflows = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.account_has_workflows);

const makeSelectAccountHasLegacyWorkflows = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.account_has_legacy_workflows,
  );

const makeSelectAccountWhatsappEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.whats_app_enabled);

const makeSelectAccountCanCreateStandardWorkflowTemplatesEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.can_create_standard_workflow_templates,
  );

const makeSelectTimeBasedDuplicateHandlingEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.time_based_duplicate_handling,
  );

const makeSelectFeatureAutoGenerateDummyEmailEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.auto_generate_dummy_email_enabled,
  );

/* Fountain AI feature flags start */

const makeSelectIsAccountFountainAiEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.fountain_ai_enabled);

const makeSelectFountainAiFaqEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.fountain_ai_faq_enabled,
  );

const makeSelectFeatureChatbotAdminEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.chatbot_admin_enabled);

const makeSelectFeatureChatbotAutomatedResponseEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.chatbot_automated_response_enabled,
  );

const makeSelectIsAccountFaqBotEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.is_faq_bot_enabled);

const makeSelectFeatureChatbotReviewEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.chatbot_review_enabled);

const makeSelectFountainAiCarreerSiteSettingEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.fountain_ai_carreer_site_setting_enabled,
  );

const makeSelectFountainAiCareerSiteEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.fountain_ai_career_site_enabled,
  );

/* Fountain AI feature flags end */

const makeSelectIsPostInterviewRecruiterNotificationsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.post_interview_recruiter_notifications_enabled,
  );

// whoami.perms is absent until WHOAMI_SUCCESS lands (initialState.whoami has no
// perms key, despite the non-optional WhoAmI type), so default to no
// permissions rather than crashing pre-whoami consumers.
const makeSelectPerms = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.perms ?? []);

const makeSelectRole = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.role);

const makeSelectIsAdmin = () =>
  createSelector([makeSelectRole()], role => role === ADMIN_ROLE);

const makeSelectHasBillingAccess = () =>
  createSelector(
    [makeSelectWhoami(), makeSelectPerms()],
    (whoami, perms) => whoami.is_owner || perms.includes('billing'),
  );

const makeSelectAccountMenuItems = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.menu);

const makeSelectAccountSlug = () =>
  createSelector([makeSelectBrands()], brands => {
    const accountBrand = brands.find(brand => brand.isAccount);
    return accountBrand ? accountBrand.slug : null;
  });

const makeSelectAccount = () =>
  createSelector([makeSelectBrands()], brands => {
    const accountBrand = brands.find(brand => brand.isAccount);
    return accountBrand ?? null;
  });

const makeSelectCurrentBrand = () =>
  createSelector(selectBrands, brands =>
    brands.find(brand => brand.isSelected),
  );

const makeSelectDefaultBrandId = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.default_brand_id);

const makeSelectIsSuperAdmin = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.is_super_admin);

const makeSelectAccounts = () =>
  createSelector(selectAuthDomain, authState => authState.superAdmin.accounts);

const makeSelectSelectedUser = () =>
  createSelector(selectAuthDomain, authState =>
    authState.superAdmin.users.find(user => user.isSelected),
  );

const makeSelectUsers = () =>
  createSelector(selectAuthDomain, authState => authState.superAdmin.users);

const makeSelectIsImpersonating = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.is_impersonated);

const makeSelectSuperAdminUserEmail = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.true_user_email) || '';

const makeSelectSignedOut = () =>
  createSelector(selectAuthDomain, authState => authState.signedOut);

const makeSelectIsFetchingWhoami = () =>
  createSelector(selectAuthDomain, authState => authState.isLoading);

const makeSelectIsGoEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.go_enabled);

const makeSelectFirstJobId = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.first_job_id);

const makeSelectEnforceBrandBasedPermissions = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.enforce_brand_based_permissions,
  );

const makeSelectCountryCode = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.country_code);

const makeSelectPolicies = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.policies);

const makeSelectIsRegularUser = () =>
  createSelector([makeSelectWhoami()], whoami => {
    if (
      whoami.role === ADMIN_ROLE ||
      whoami.is_super_admin ||
      whoami.is_owner
    ) {
      return false;
    }
    return true;
  });

const makeSelectClientSettingsUpdateSuccess = () =>
  createSelector(
    selectAuthDomain,
    authState => authState.clientSettingsUpdateSuccess,
  );

const makeSelectEnvVars = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.recruiter_ui_env_vars);

const makeSelectUavUserBadgeDisabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['disable-uav-user-badge'],
  );

// Whether the WX platform (unified) left navbar is shown. Must stay the
// single source of truth: surfaces that hide or move entries because the
// unified navbar covers them (e.g. the top-nav Universal Applicant View)
// must use the same predicate that mounts the navbar itself.
const makeSelectUnifiedNavigationEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami =>
      !!whoami.account_unified_auth &&
      !whoami.feature_flags?.['unified-navigation-exclusion'],
  );

const makeSelectLastFunnelSlug = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.current_funnel_slug);

const makeSelectLastFunnelId = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.current_funnel_id);

const makeSelectLookerCustomReportsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.looker_custom_reports_enabled,
  );

const makeSelectGetMoreTextToApplyEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.get_more_text_to_apply_enabled,
  );

const makeSelectHiringGoalsEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.hiring_goals_enabled);

const makeSelectRequireActiveHiringGoalForOpenings = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.require_active_hiring_goal_for_openings,
  );

const makeSelectHiringGoalsV3Enabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.hiring_goals_v3_enabled,
  );

const makeSelectAdditionalOpeningDetailsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['additional-opening-details'],
  );

const makeSelectApplicantPortalJavascriptEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['applicant-portal-javascript'],
  );

const makeSelectPipelineStagesEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['pipeline-stages'],
  );

const makeSelectPoolInHireEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['pool-in-hire'],
  );

const makeSelectHiringGoalsV4Enabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['hiring-goals-v4'],
  );

const makeSelectHidePayByInvoiceInHireEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['hide-pay-by-invoice-in-hire'],
  );

const makeHasSubAccounts = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.has_sub_accounts);

const makeSelectDesktopNotificationEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.desktop_notification_enabled,
  );

const makeAvailableSlotsNewAvailabilityMessage = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['available-slots-new-availability-message'],
  );

const makeSelectWorkflowSystemEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['workflow-system'],
  );

const makeSelectIsEmbeddedDocusignEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['embedded-docusign'],
  );

const makeSelectCustomerAttributesEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['manage-customer-attributes'],
  );

const makeSelectImmediateHiringDecision = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['immediate-hiring-decision'],
  );

const makeSelectHasRestrictedFunnelAccess = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.has_restricted_funnel_access,
  );

const makeSelectAccountId = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.account_id);

const makeIsHiringGoalsV2Enabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['hiring-goals-v2'],
  );

const makeSelectIsOpeningStatusIconV2Enabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['opening-status-icon-2-0'],
  );

const makeIsCannotRepeatStandardKeyEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['cannot-repeat-standard-key'] ?? false,
  );

const makeIsUpsFunctionalityEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['ups-functionality'] ?? false,
  );

const makeSelectSlotAvailabilityBasedOpeningsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.slot_availability_based_openings_enabled,
  );

// Either engine. `opening_approval_enabled` is the legacy `opening-approvals`
// flag; `opening_approval_control_enabled` is the Common Approval Service gate.
// An account runs one or the other, and the surfaces reading this selector — the
// openings-table status filter, the job-settings link, the approvals route —
// serve both.
const makeSelectOpeningApprovalEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami =>
      Boolean(
        whoami.opening_approval_enabled ||
          whoami.opening_approval_control_enabled,
      ),
  );

const makeSelectOpeningApprovalsImprovementsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['opening-approvals-improvements'],
  );

const makeSelectOpeningsTableCustomizeColumnsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['openings-table-customize-columns'],
  );

const makeSelectOpeningsTableSavedViewsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['openings-table-saved-views'],
  );

const makeSelectOpeningsTableBulkActionsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['openings-table-bulk-actions'],
  );

// Role-level "Bulk edit" opening permission (FunnelPolicy#bulk_editing?), used to
// gate the openings bulk-actions UI in addition to the feature flag.
const makeSelectBulkEditingEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.bulk_editing_enabled);

const makeSelectQrCodeForOpeningEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.qr_code_for_opening_enabled,
  );

const makeSelectOpeningCsvUpdate = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['opening-csv-update'],
  );

const makeSelectWorkflowEditorPartnerStagePreview = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['workflow-editor-partner-stage-preview'],
  );

const makeSelectCustomerAttributesRulesEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['customer-attributes-rules'],
  );

const makeSelectCanSendOnlyTemplatesInSms = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.can_send_only_templates_in_sms,
  );

const makeIsSmsLengthLimitEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['sms-length-limited'],
  );

const makeShowColumnsDrawerInSessions = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['show-columns-drawer-in-sessions'],
  );

const makeIsDelaySendingWebhookEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['hold-data-collection-partner-webhooks'],
  );

const makeIsCandidateAiEnabled = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.cai_agent_enabled);

const makeIsCandidateAiApplyV2Enabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['cai-show-apply-v-2'],
  );

const makeIsCaiKnowledgeBaseV2Enabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['cai-show-knowledge-base-v-2'],
  );

const makeIsReferralsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['referral-product'],
  );

const makeIsDuplicateCheckInRuleStageEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['applicant-duplicate-check-in-rule-stage'],
  );

const makeIsWorkrampHelpCenterSSOEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['workramp-help-center-sso'],
  );

const makeSelectHideInactiveOpenings = () =>
  createSelector([makeSelectWhoami()], whoami => whoami.hide_inactive_openings);

const makeIsInternalCareerSiteEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['internal-career-site'],
  );

const makeIsCareerSiteTranslationEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['career-site-translation'],
  );

const makeIsCareerSiteSettingsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['career-site-settings'],
  );

const makeIsZipRecruiterIntegrationEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['zip-recruiter-integration'] ?? false,
  );

/** True when user can access the Opening Attributes page (view/edit). */
const makeSelectCanAccessJobAttributes = () =>
  createSelector(
    [makeSelectPerms(), makeSelectIsAdmin(), makeSelectIsSuperAdmin()],
    (perms, isAdmin, isSuperAdmin) =>
      perms.includes('view_job_attributes') ||
      perms.includes('manage_attributes') ||
      perms.includes('set_values_for_attributes') ||
      isAdmin ||
      isSuperAdmin,
  );

const makeIsCareerSiteInterdependentFiltersEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami =>
      whoami.feature_flags['career-site-interdependent-filters'] ?? false,
  );

const makeIsOpeningJobDescriptionEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['opening-job-description'],
  );

const makeApplicantGroupedDetailsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['applicant-grouped-details'],
  );

const makeCollapseApplicantRowsEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['collapse-applicant-rows'],
  );

const makeIsCustomTerminologiesEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['custom-terminologies'],
  );

const makeIsMergeKeyTranslationControlEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['merge-key-translation-control'],
  );

const makeUniversalRecruiterDashboardEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['universal-recruiter-dashboard'],
  );

const makeScoreCardTemplatesEnabled = () =>
  createSelector(
    [makeSelectWhoami()],
    whoami => whoami.feature_flags['score-card-templates'],
  );

export {
  makeApplicantGroupedDetailsEnabled,
  makeAvailableSlotsNewAvailabilityMessage,
  makeCollapseApplicantRowsEnabled,
  makeHasSubAccounts,
  makeIsAllBrandsFetched,
  makeIsCaiKnowledgeBaseV2Enabled,
  makeIsCandidateAiApplyV2Enabled,
  makeIsCandidateAiEnabled,
  makeIsCannotRepeatStandardKeyEnabled,
  makeIsCareerSiteInterdependentFiltersEnabled,
  makeIsCareerSiteSettingsEnabled,
  makeIsCareerSiteTranslationEnabled,
  makeIsCustomTerminologiesEnabled,
  makeIsMergeKeyTranslationControlEnabled,
  makeIsDelaySendingWebhookEnabled,
  makeIsDuplicateCheckInRuleStageEnabled,
  makeIsFetchingAllBrands,
  makeIsHiringGoalsV2Enabled,
  makeIsInternalCareerSiteEnabled,
  makeIsOpeningJobDescriptionEnabled,
  makeIsReferralsEnabled,
  makeIsSmsLengthLimitEnabled,
  makeIsUpsFunctionalityEnabled,
  makeIsWorkrampHelpCenterSSOEnabled,
  makeIsZipRecruiterIntegrationEnabled,
  makeScoreCardTemplatesEnabled,
  makeSelectAccessibleAccountSlugs,
  makeSelectAccount,
  makeSelectAccountCanCreateStandardWorkflowTemplatesEnabled,
  makeSelectAccountHasLegacyWorkflows,
  makeSelectAccountHasWorkflows,
  makeSelectAccountId,
  makeSelectAccountMenuItems,
  makeSelectAccountName,
  makeSelectAccounts,
  makeSelectAccountSlug,
  makeSelectAccountSmsEnabled,
  makeSelectAccountWhatsappEnabled,
  makeSelectAdditionalOpeningDetailsEnabled,
  makeSelectApplicantPortalJavascriptEnabled,
  makeSelectBrands,
  makeSelectCanAccessJobAttributes,
  makeSelectCanSendOnlyTemplatesInSms,
  makeSelectClientSettings,
  makeSelectClientSettingsUpdateSuccess,
  makeSelectCountryCode,
  makeSelectCurrentBrand,
  makeSelectCurrentLocation,
  makeSelectCustomerAttributesEnabled,
  makeSelectCustomerAttributesRulesEnabled,
  makeSelectDefaultBrandId,
  makeSelectDesktopNotificationEnabled,
  makeSelectEmail,
  makeSelectEnforceBrandBasedPermissions,
  makeSelectEnvVars,
  makeSelectExternalId,
  makeSelectFeatureAutoGenerateDummyEmailEnabled,
  makeSelectFeatureChatbotAdminEnabled,
  makeSelectFeatureChatbotAutomatedResponseEnabled,
  makeSelectFeatureChatbotReviewEnabled,
  makeSelectFirstJobId,
  makeSelectFountainAiCareerSiteEnabled,
  makeSelectFountainAiCarreerSiteSettingEnabled,
  makeSelectFountainAiFaqEnabled,
  makeSelectGetMoreTextToApplyEnabled,
  makeSelectHasBillingAccess,
  makeSelectHasRestrictedFunnelAccess,
  makeSelectHideInactiveOpenings,
  makeSelectHidePayByInvoiceInHireEnabled,
  makeSelectHiringGoalsEnabled,
  makeSelectHiringGoalsV3Enabled,
  makeSelectHiringGoalsV4Enabled,
  makeSelectImmediateHiringDecision,
  makeSelectIsAccountFaqBotEnabled,
  makeSelectIsAccountFountainAiEnabled,
  makeSelectIsAdmin,
  makeSelectIsEmbeddedDocusignEnabled,
  makeSelectIsFetchingWhoami,
  makeSelectIsGoEnabled,
  makeSelectIsImpersonating,
  makeSelectIsLoggedIn,
  makeSelectIsOpeningStatusIconV2Enabled,
  makeSelectIsPostInterviewRecruiterNotificationsEnabled,
  makeSelectIsRegularUser,
  makeSelectIsSuperAdmin,
  makeSelectIsUpdatingClientSettings,
  makeSelectLastFunnelId,
  makeSelectLastFunnelSlug,
  makeSelectLocations,
  makeSelectLookerCustomReportsEnabled,
  makeSelectOpeningApprovalEnabled,
  makeSelectOpeningApprovalsImprovementsEnabled,
  makeSelectOpeningCsvUpdate,
  makeSelectOpeningsTableBulkActionsEnabled,
  makeSelectBulkEditingEnabled,
  makeSelectOpeningsTableCustomizeColumnsEnabled,
  makeSelectOpeningsTableSavedViewsEnabled,
  makeSelectPerms,
  makeSelectPhoneNumber,
  makeSelectPipelineStagesEnabled,
  makeSelectPolicies,
  makeSelectPoolInHireEnabled,
  makeSelectQrCodeForOpeningEnabled,
  makeSelectRequireActiveHiringGoalForOpenings,
  makeSelectRole,
  makeSelectSelectedAccountSlug,
  makeSelectSelectedUser,
  makeSelectSignedOut,
  makeSelectSlotAvailabilityBasedOpeningsEnabled,
  makeSelectSuperAdminUserEmail,
  makeSelectTelephonyEnabled,
  makeSelectTimeBasedDuplicateHandlingEnabled,
  makeSelectUavUserBadgeDisabled,
  makeSelectUnifiedNavigationEnabled,
  makeSelectUnreadSms,
  makeSelectUserName,
  makeSelectUsers,
  makeSelectWhoami,
  makeSelectWhoamiError,
  makeSelectWorkflowEditorPartnerStagePreview,
  makeSelectWorkflowSystemEnabled,
  makeShowColumnsDrawerInSessions,
  makeUniversalRecruiterDashboardEnabled,
};
